import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import axios from "axios";
import "../../ClientBookings/clientBookings.css";

export default function BookingsList({ propertyId }) {
  const navigate = useNavigate();

  // `view` is null until the ongoing peek decides whether to start on Ongoing
  // or Upcoming — prevents the toggle from rendering in a wrong-default state
  // and then "snapping" once the peek resolves.
  const [view, setView] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [bookings, setBookings] = useState([]);
  // Start in a loading state so the content area shows the spinner immediately
  // while the peek runs and the first bookings fetch is queued.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalPages, setTotalPages] = useState(1);

  // Peek ongoing count so we know whether to render the Ongoing tab
  // and whether to default to it on first render.
  const [ongoingCount, setOngoingCount] = useState(0);
  const initialisedRef = useRef(false);

  const getStatusClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "completed" || s === "confirmed") return "cb-statusChip--green";
    if (s === "cancelled" || s === "rescheduled") return "cb-statusChip--red";
    if (s === "pending" || s === "in_progress" || s === "in process") return "cb-statusChip--orange";
    return "cb-statusChip--grey";
  };

  const fetchBookings = useCallback(async () => {
    if (!propertyId) return;
    // Don't fetch while the peek is still deciding the initial view.
    if (!view) return;

    setIsLoading(true);
    setError("");
    setBookings([]);

    try {
      const endpoint = `/api/client-requests/bookings/all/${view}?page=${currentPage}&property_id=${propertyId}`;
      const response = await axios.get(endpoint);

      setBookings(Array.isArray(response.data?.bookings) ? response.data.bookings : []);
      setTotalPages(response.data?.pagination?.totalPages || 1);
    } catch (err) {
      console.error("Error fetching property bookings:", err);
      setError("Failed to load bookings. Please try again.");
      setBookings([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [propertyId, view, currentPage]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Fire once per mount: fetch ongoing count + set the default view in a single
  // render commit (picking ongoing when there's live activity, otherwise future).
  const fetchOngoingCount = useCallback(async () => {
    if (!propertyId) return;
    try {
      const res = await axios.get(
        `/api/client-requests/bookings/all/ongoing?page=1&property_id=${propertyId}`,
      );
      const total = res.data?.pagination?.totalBookings ?? 0;
      setOngoingCount(total);
      if (!initialisedRef.current) {
        initialisedRef.current = true;
        setView(total > 0 ? "ongoing" : "future");
      }
    } catch (err) {
      console.error("Error fetching ongoing count:", err);
      if (!initialisedRef.current) {
        initialisedRef.current = true;
        setView("future"); // safe fallback — don't leave the user stuck on a spinner
      }
    }
  }, [propertyId]);

  useEffect(() => {
    fetchOngoingCount();
  }, [fetchOngoingCount]);

  const viewLabel = useMemo(() => {
    if (view === "ongoing") return "Ongoing Bookings";
    if (view === "past") return "Past Bookings";
    if (view === "future") return "Upcoming Bookings";
    return "\u00A0"; // keep line height stable while resolving
  }, [view]);

  const showOngoingTab = ongoingCount > 0 || view === "ongoing";
  const viewReady = view !== null;

  return (
    <>
      <div className="cpdCard__head cpdCard__head--split">
        <div>
          <h2 className="cpdCard__title">Bookings</h2>
          <div className="cpdCard__sub">{viewLabel}</div>
        </div>

        <div className="cpdHeadRight">
          <div className={`cb__toggle cpdToggleCompact ${viewReady ? "" : "cb__toggle--loading"}`}>
            {!viewReady ? (
              <span className="cb__togglePlaceholder" aria-hidden="true" />
            ) : (
              <>
            {showOngoingTab && (
              <button
                className={`cb__toggleBtn cb__toggleBtn--ongoing ${view === "ongoing" ? "is-active" : ""}`}
                onClick={() => {
                  setView("ongoing");
                  setCurrentPage(1);
                }}
                type="button"
                disabled={isLoading}
              >
                <span className="cb__toggleDot" aria-hidden="true" /> Ongoing
              </button>
            )}

            <button
              className={`cb__toggleBtn ${view === "future" ? "is-active" : ""}`}
              onClick={() => {
                setView("future");
                setCurrentPage(1);
              }}
              type="button"
              disabled={isLoading}
            >
              Upcoming
            </button>

            <button
              className={`cb__toggleBtn ${view === "past" ? "is-active" : ""}`}
              onClick={() => {
                setView("past");
                setCurrentPage(1);
              }}
              type="button"
              disabled={isLoading}
            >
              Past
            </button>
              </>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="cb__loading">
          <div className="app-loading">
            <div className="app-spinner" />
            <div className="app-spinner-text">Loading bookings…</div>
          </div>
        </div>
      ) : error ? (
        <div className="cb__error">{error}</div>
      ) : bookings.length === 0 ? (
        <div className="client-bookings-empty">
          <div className="client-bookings-empty__icon">
            {view === "ongoing" ? "⏱️" : "🗓️"}
          </div>
          <div className="client-bookings-empty__title">
            {view === "ongoing"
              ? "No ongoing cleanings"
              : view === "future"
              ? "No upcoming bookings"
              : "No past bookings"}
          </div>
          <div className="client-bookings-empty__text">
            {view === "ongoing"
              ? "Anything being worked on right now will appear here."
              : view === "future"
              ? "When a booking is scheduled, it will appear here."
              : "Once bookings are completed, you'll see them listed here."}
          </div>
        </div>
      ) : (
        <>
          <div className="cb__list">
            {bookings.map((booking) => {
              const bookingId = booking.booking_id ?? booking.id;

              const dt = DateTime.fromISO(booking.booking_start, { zone: "utc" }).setZone("Asia/Dubai");
              const day = dt.isValid ? dt.toFormat("dd") : "—";
              const month = dt.isValid ? dt.toFormat("LLL") : "—";
              const year = dt.isValid ? dt.toFormat("yyyy") : "—";
              const time = dt.isValid ? dt.toFormat("HH:mm") : "—";

              return (
                <div
                  className="cbCard"
                  key={bookingId}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/client/bookings/${bookingId}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") navigate(`/client/bookings/${bookingId}`);
                  }}
                  title="Open booking"
                >
                  <div className="cbCard__date">
                    <div className="cbCard__day">{day}</div>
                    <div className="cbCard__month">{month}</div>
                    <div className="cbCard__year">{year}</div>
                  </div>

                  <div className="cbCard__main">
                    <div className="cbCard__top">
                      <div className="cbCard__id">Booking #{bookingId}</div>
                      <div className="cbCard__prop">
                        {booking.unit_number ? `Unit ${booking.unit_number} · ${booking.building_name}` : booking.building_name}
                      </div>
                    </div>

                    <div className="cbCard__meta">
                      <div className="cbMeta">
                        <div className="cbMeta__label">Time</div>
                        <div className="cbMeta__value">{time}</div>
                      </div>

                      <div className="cbMeta">
                        <div className="cbMeta__label">Duration</div>
                        <div className="cbMeta__value">{booking.duration_hours ?? "—"} hrs</div>
                      </div>

                      <div className="cbMeta cbMeta--wide">
                        <div className="cbMeta__label">Type</div>
                        <div className="cbMeta__value">{booking.booking_type || "—"}</div>
                      </div>
                    </div>
                  </div>

                  <div className="cbCard__side">
                    <span className={`cb-statusChip ${getStatusClass(booking.booking_status)}`}>
                      {booking.booking_status || "—"}
                    </span>

                    <button
                      type="button"
                      className="cbBtnGhost cbBtnGhost--tight"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/client/bookings/${bookingId}`);
                      }}
                    >
                      View
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 ? (
            <div className="cb__pagination">
              <button
                className="cbBtnGhost"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                type="button"
              >
                Previous
              </button>

              <div className="cb__pageInfo">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </div>

              <button
                className="cbBtnGhost"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                type="button"
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}